import { Settings, CircleHelp, SquareTerminal, ListChecks, HistoryIcon, HomeIcon, PlugIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
  SidebarMenuItem,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuBadge,
  SidebarGroupLabel,
  SidebarGroup,
  SidebarGroupContent,
} from '@/components/ui/sidebar';
import { IconLogo, Logo } from 'src/components/Svg/Images';
import { UserRoleEnum } from 'src/graphql/schema';
import { isLinkActive } from 'src/utils/is-link-active';

import { NavApplications, NavUser } from './ui';

const footerItems: {
  icon: React.ElementType;
  label: string;
  to: string;
}[] = [
  {
    icon: CircleHelp,
    label: 'Help and resources',
    to: '/help-resources',
  },
  {
    icon: Settings,
    label: 'Settings',
    to: '/settings',
  },
];

type Props = React.ComponentProps<typeof Sidebar> & {
  user: {
    name: string;
    email: string;
    role: UserRoleEnum;
  };
  companyName: string;
  migrationsCount: number;
  withIntegrations?: boolean;
  withAuditLogs?: boolean;
  tasksCount: number;
  applicationsSubmenu?: React.ReactNode;
};

export function AppSidebar({
  user,
  companyName,
  migrationsCount,
  tasksCount,
  withIntegrations = false,
  withAuditLogs = false,
  applicationsSubmenu,
  ...props
}: Props) {
  const { pathname } = useLocation();
  const { open, isMobile, toggleSidebar } = useSidebar();

  return (
    <Sidebar {...props} collapsible="icon">
      <SidebarHeader>
        <Link to="/" onClick={isMobile ? toggleSidebar : undefined}>
          {open || isMobile ? <Logo className="w-[130px] h-[40px]" /> : <IconLogo className="w-8 h-8 mb-2" />}
        </Link>
      </SidebarHeader>
      <SidebarContent className="gap-0 pb-0">
        <SidebarGroup className="flex-1">
          <SidebarGroupLabel className="text-2xs font-extrabold uppercase tracking-[0.07em]">main</SidebarGroupLabel>
          <SidebarGroupContent className="flex flex-1 flex-col">
            <SidebarMenu className="min-h-43 flex-1">
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isLinkActive(pathname, '/')}
                  asChild
                  tooltip="Home"
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <Link to="/">
                    <HomeIcon />
                    Home
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <NavApplications
                isActive={isLinkActive(pathname, '/applications')}
                onNavigate={isMobile ? toggleSidebar : undefined}
              >
                {applicationsSubmenu}
              </NavApplications>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="pb-0">
          <SidebarGroupLabel className="text-2xs font-extrabold uppercase tracking-[0.07em]">Manage</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="text-foreground data-menu-button:hover:text-foreground">
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isLinkActive(pathname, '/tasks')}
                  tooltip="Tasks"
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <Link to="/tasks">
                    <ListChecks /> Tasks
                    {Boolean(tasksCount) && (
                      <SidebarMenuBadge className="bg-destructive text-white group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:-translate-y-[13px] group-data-[collapsible=icon]:translate-x-[10px] transition-transform text-2xs p-1 h-4 rounded-full">
                        {tasksCount > 9 ? '9+' : tasksCount}
                      </SidebarMenuBadge>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {withAuditLogs && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isLinkActive(pathname, '/activity-log')}
                    tooltip="Activity log"
                    onClick={isMobile ? toggleSidebar : undefined}
                  >
                    <Link to="/activity-log">
                      <HistoryIcon /> Activity log
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {withIntegrations && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isLinkActive(pathname, '/integrations')}
                    tooltip="Integrations"
                    onClick={isMobile ? toggleSidebar : undefined}
                  >
                    <Link to="/integrations">
                      <PlugIcon /> Integrations
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {Boolean(migrationsCount) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isLinkActive(pathname, '/migrations')}
                    tooltip="Migrations"
                    onClick={isMobile ? toggleSidebar : undefined}
                  >
                    <Link to="/migrations">
                      <SquareTerminal /> Migrations
                      <SidebarMenuBadge>{migrationsCount}</SidebarMenuBadge>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="pt-0">
          <SidebarGroupLabel className="text-2xs font-extrabold uppercase tracking-[0.07em]">Account</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="text-foreground data-menu-button:hover:text-foreground">
              {footerItems.map((menu) => (
                <SidebarMenuItem key={menu.label}>
                  <SidebarMenuButton
                    asChild
                    isActive={isLinkActive(pathname, menu.to)}
                    tooltip={menu.label}
                    onClick={isMobile ? toggleSidebar : undefined}
                  >
                    <Link to={menu.to}>
                      <menu.icon /> {menu.label}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="py-0">
        <NavUser user={user} companyName={companyName} />
      </SidebarFooter>
    </Sidebar>
  );
}
